import React, { useState } from 'react';
import { MapPin, User, DollarSign, Users, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Provider } from '../../types';

interface ProviderCardProps {
  provider: Provider | any; // Using any for grouped provider
  compact?: boolean;
  grouped?: boolean;
}

export const ProviderCard: React.FC<ProviderCardProps> = ({ provider, compact = true, grouped = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num?: number) => {
    if (!num) return 'N/A';
    return new Intl.NumberFormat('en-US').format(num);
  };

  if (compact && grouped) {
    return (
      <>
        {/* Main Row - Clickable */}
        <div 
          className="border-b border-border hover:bg-accent/50 transition-colors cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="p-4 grid grid-cols-12 gap-3 items-start">
            {/* Expand Icon & Provider Name - 3 cols on desktop, 4 on tablet */}
            <div className="col-span-12 md:col-span-4 lg:col-span-3 flex items-start gap-2">
              <button className="mt-0.5">
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-primary">{provider.name}</div>
                {provider.credentials && (
                  <div className="text-sm text-muted-foreground">{provider.credentials}</div>
                )}
              </div>
            </div>

            {/* NPI - 1 col, hidden on mobile */}
            <div className="hidden md:block md:col-span-2 lg:col-span-1 text-sm text-muted-foreground">
              {provider.npi}
            </div>

            {/* Address - 3 cols on desktop, 4 on tablet */}
            <div className="col-span-12 md:col-span-4 lg:col-span-3 text-sm">
              <div className="flex items-start gap-1">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="leading-tight min-w-0">
                  {provider.address.street1 && (
                    <div className="truncate">{provider.address.street1}</div>
                  )}
                  <div className="break-words">{provider.address.city}, {provider.address.state} {provider.address.zip}</div>
                </div>
              </div>
            </div>

            {/* Service Count - 2 cols, hidden on mobile */}
            <div className="hidden lg:block lg:col-span-2 text-sm">
              <div className="font-medium">{provider.serviceCount} Services</div>
            </div>

            {/* Aggregated Stats - 3 cols on desktop, full width on tablet */}
            <div className="col-span-12 md:col-span-2 lg:col-span-3 mt-2 md:mt-0">
              <div className="grid grid-cols-2 gap-x-3 text-sm">
                {/* First Row */}
                <div className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs">
                    <span className="font-medium">{formatNumber(provider.totalBeneficiaries)}</span> total
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs">
                    <span className="font-medium">{formatNumber(provider.totalServices)}</span> total
                  </span>
                </div>
                
                {/* Second Row */}
                <div className="flex items-center gap-1 mt-1 col-span-2">
                  <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs">
                    Total Charge: <span className="font-medium">{formatCurrency(provider.totalCharge || 0)}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Expanded Services */}
        {isExpanded && (
          <div className="bg-muted/30 border-b border-border">
            <div className="p-4 pl-12">
              <div className="text-xs font-medium text-muted-foreground mb-2">Service Details</div>
              <div className="space-y-2">
                {provider.services.map((service: any, index: number) => (
                  <div key={index} className="py-2 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm w-20">{service.hcpcsCode}</div>
                      <div className="flex-1 grid grid-cols-3 gap-4 text-xs">
                        {/* Column 1 - Volume Metrics */}
                        <div className="flex items-center gap-4">
                          <span className="text-muted-foreground">Beneficiaries:</span>
                          <span className="font-medium">{formatNumber(service.totalBeneficiaries)}</span>
                          <span className="text-muted-foreground">Services:</span>
                          <span className="font-medium">{formatNumber(service.totalServices)}</span>
                        </div>
                        
                        {/* Column 2 - Medicare Payments */}
                        <div className="flex items-center gap-4">
                          <span className="text-muted-foreground">Allowed:</span>
                          <span className="font-medium">{formatCurrency(service.avgMedicareAllowed || service.avgMedicareAllowedAmt)}</span>
                          <span className="text-muted-foreground">Paid:</span>
                          <span className="font-medium">{formatCurrency(service.avgMedicarePayment)}</span>
                        </div>
                        
                        {/* Column 3 - Charges */}
                        <div className="flex items-center gap-4">
                          <span className="text-muted-foreground">Avg Charge:</span>
                          <span className="font-medium">{formatCurrency(service.avgSubmittedCharge)}</span>
                          <span className="text-muted-foreground">Total:</span>
                          <span className="font-medium text-primary">{formatCurrency((service.avgSubmittedCharge || 0) * (service.totalServices || 0))}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  if (compact) {
    return (
      <div className="border-b border-border hover:bg-accent/50 transition-colors">
        <div className="p-4 grid grid-cols-12 gap-3 items-start">
          {/* Provider Name & Credentials - 3 cols on desktop, 4 on tablet */}
          <div className="col-span-12 md:col-span-4 lg:col-span-3">
            <div className="font-medium text-primary">{provider.name}</div>
            {provider.credentials && (
              <div className="text-sm text-muted-foreground">{provider.credentials}</div>
            )}
          </div>

          {/* NPI - 1 col, hidden on mobile */}
          <div className="hidden md:block md:col-span-2 lg:col-span-1 text-sm text-muted-foreground">
            {provider.npi}
          </div>

          {/* Address - 3 cols on desktop, 4 on tablet */}
          <div className="col-span-12 md:col-span-4 lg:col-span-3 text-sm">
            <div className="flex items-start gap-1">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="leading-tight min-w-0">
                {provider.address.street1 && (
                  <div className="truncate">{provider.address.street1}</div>
                )}
                <div className="break-words">{provider.address.city}, {provider.address.state} {provider.address.zip}</div>
              </div>
            </div>
          </div>

          {/* Service Info - 2 cols, hidden on mobile */}
          <div className="hidden lg:block lg:col-span-2 text-sm">
            {provider.services.hcpcsCode && (
              <div className="font-medium">{provider.services.hcpcsCode}</div>
            )}
          </div>

          {/* Stats - 3 cols on desktop, full width on tablet */}
          <div className="col-span-12 md:col-span-2 lg:col-span-3 mt-2 md:mt-0">
            <div className="grid grid-cols-2 gap-x-3 text-sm">
              {/* First Row */}
              <div className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs">
                  <span className="font-medium">{formatNumber(provider.services.totalBeneficiaries)}</span> patients
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs">
                  <span className="font-medium">{formatNumber(provider.services.totalServices)}</span> services
                </span>
              </div>
              
              {/* Second Row */}
              <div className="flex items-center gap-1 mt-1">
                <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs">
                  <span className="font-medium">{formatCurrency(provider.services.avgMedicarePayment)}</span>
                </span>
              </div>
              {provider.distance ? (
                <div className="flex items-center gap-1 mt-1 text-primary">
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="text-xs font-medium">{provider.distance} miles</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-muted-foreground">
                    Avg: {formatCurrency(provider.services.avgSubmittedCharge)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Original card layout (for detailed view if needed)
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <CardTitle className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-primary">{provider.name}</h3>
            {provider.credentials && (
              <p className="text-sm text-muted-foreground">{provider.credentials}</p>
            )}
          </div>
          {provider.distance && (
            <div className="text-right">
              <span className="text-sm font-medium text-primary">{provider.distance} mi</span>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Provider Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">NPI:</span>
              <span>{provider.npi}</span>
            </div>
            
            {provider.type && (
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="font-medium">Type:</span>
                <span>{provider.type}</span>
              </div>
            )}
            
            {provider.medicareParticipating && (
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="font-medium">Medicare:</span>
                <span className={provider.medicareParticipating === 'Y' ? 'text-green-600' : 'text-red-600'}>
                  {provider.medicareParticipating === 'Y' ? 'Participating' : 'Non-participating'}
                </span>
              </div>
            )}
          </div>

          {/* Address */}
          <div>
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                {provider.address.street1 && <div>{provider.address.street1}</div>}
                {provider.address.street2 && <div>{provider.address.street2}</div>}
                <div>
                  {provider.address.city && `${provider.address.city}, `}
                  {provider.address.state} {provider.address.zip}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Information */}
        {provider.services && (
          <div className="border-t pt-4">
            <h4 className="font-medium text-sm mb-3">Service Details</h4>
            
            {provider.services.description && (
              <div className="mb-3">
                <span className="font-medium text-sm">Service: </span>
                <span className="text-sm">{provider.services.description}</span>
                {provider.services.hcpcsCode && (
                  <span className="text-xs text-muted-foreground ml-2">({provider.services.hcpcsCode})</span>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center p-2 bg-muted/50 rounded">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Users className="h-3 w-3" />
                  <span className="font-medium">Beneficiaries</span>
                </div>
                <div className="text-lg font-semibold">
                  {formatNumber(provider.services.totalBeneficiaries)}
                </div>
              </div>

              <div className="text-center p-2 bg-muted/50 rounded">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <span className="font-medium">Services</span>
                </div>
                <div className="text-lg font-semibold">
                  {formatNumber(provider.services.totalServices)}
                </div>
              </div>

              <div className="text-center p-2 bg-muted/50 rounded">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <DollarSign className="h-3 w-3" />
                  <span className="font-medium">Avg Charge</span>
                </div>
                <div className="text-sm font-semibold">
                  {formatCurrency(provider.services.avgSubmittedCharge)}
                </div>
              </div>

              <div className="text-center p-2 bg-muted/50 rounded">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <DollarSign className="h-3 w-3" />
                  <span className="font-medium">Medicare Paid</span>
                </div>
                <div className="text-sm font-semibold">
                  {formatCurrency(provider.services.avgMedicarePayment)}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
